import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Loader2, ArrowRight, ArrowLeft, Save, LogOut, CheckCircle, CheckCircle2, ScanSearch } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_readiness-check-27/artifacts/el8npnpt_CXOlogo.png";
const OVERRIDE_REASON_MIN = 15;

const AssessmentPage = () => {
  const navigate = useNavigate();
  const { user, orgProfile, logout, getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [assessmentId, setAssessmentId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentPillar, setCurrentPillar] = useState(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [skippedQuestions, setSkippedQuestions] = useState([]);
  const [hintsByQuestion, setHintsByQuestion] = useState({});
  const [confirmationsByQuestion, setConfirmationsByQuestion] = useState({});
  const [overrideReasons, setOverrideReasons] = useState({});
  const [unsaved, setUnsaved] = useState({});

  useEffect(() => {
    if (!orgProfile) {
      navigate('/org-profile');
      return;
    }
    initializeAssessment();
  }, [orgProfile, navigate]);

  const initializeAssessment = async () => {
    try {
      // Start or resume assessment
      const startResponse = await axios.post(
        `${API}/assessment/start`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setAssessmentId(startResponse.data.assessment_id);

      // Stage 0 gate: the pillar stepper opens only once Evidence Discovery is
      // locked (or was never applicable for this assessment).
      const discoveryStatus = startResponse.data.discovery_status;
      if (discoveryStatus && !['locked', 'not_applicable'].includes(discoveryStatus)) {
        navigate('/evidence-discovery', { replace: true });
        return;
      }

      // Get questions and pillars
      const questionsResponse = await axios.get(
        `${API}/questions`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setPillars(questionsResponse.data.pillars);
      
      // Filter out skipped questions based on org profile
      let allQuestions = questionsResponse.data.questions;
      const skipped = [];
      
      if (!orgProfile.children_data) {
        skipped.push('Q38', 'Q39');
      }
      if (!orgProfile.cross_border) {
        skipped.push('Q35');
      }
      if (!orgProfile.sensitive_data) {
        skipped.push('Q40');
      }
      if (!orgProfile.sdf_status) {
        skipped.push('Q32');
      }
      
      setSkippedQuestions(skipped);
      
      // Filter questions
      allQuestions = allQuestions.filter(q => !skipped.includes(q.question_number));
      setQuestions(allQuestions);

      // Get existing answers
      const answersResponse = await axios.get(
        `${API}/assessment/${startResponse.data.assessment_id}/answers`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      const answerMap = {};
      const reasonMap = {};
      answersResponse.data.answers.forEach(a => {
        if (!a.is_auto_scored) {
          answerMap[a.question_id] = a.answer_option;
          if (a.override_reason) reasonMap[a.question_id] = a.override_reason;
        }
      });
      setAnswers(answerMap);
      setOverrideReasons(reasonMap);

      // Stage 0 hint chips (frozen evidence). Empty for assessments that never
      // went through Stage 0 — the stepper then behaves exactly as before.
      try {
        const hintsResponse = await axios.get(
          `${API}/assessment/${startResponse.data.assessment_id}/hints`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        setHintsByQuestion(hintsResponse.data.by_question || {});
        setConfirmationsByQuestion(hintsResponse.data.confirmations_by_question || {});
      } catch (e) {
        setHintsByQuestion({});
        setConfirmationsByQuestion({});
      }

      // Set current pillar based on progress
      if (answersResponse.data.assessment?.current_pillar) {
        setCurrentPillar(answersResponse.data.assessment.current_pillar - 1);
      }
    } catch (error) {
      toast.error('Failed to load assessment');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const currentPillarData = pillars[currentPillar];
  const pillarQuestions = questions.filter(q => q.pillar_code === currentPillarData?.code);
  const totalQuestions = questions.length;
  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / totalQuestions) * 100;

  const isPillarComplete = () => {
    return pillarQuestions.every(q => answers[q.id] && !unsaved[q.id]);
  };

  const hintFor = (questionId) => hintsByQuestion[questionId]?.primary || null;

  const needsOverrideReason = (questionId, value) => {
    const hint = hintFor(questionId);
    return Boolean(hint?.suggested_option && value && value !== hint.suggested_option);
  };

  const persistAnswer = async (questionId, value, reason) => {
    try {
      await axios.post(
        `${API}/assessment/${assessmentId}/answer`,
        { question_id: questionId, answer_option: value, override_reason: reason || null },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setUnsaved(prev => {
        const next = { ...prev };
        delete next[questionId];
        return next;
      });
    } catch (error) {
      setUnsaved(prev => ({ ...prev, [questionId]: true }));
      if (error.response?.data?.detail?.code !== 'OVERRIDE_REASON_REQUIRED') {
        toast.error('Failed to save answer');
      }
    }
  };

  const handleAnswerChange = async (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    const reason = (overrideReasons[questionId] || '').trim();
    if (needsOverrideReason(questionId, value) && reason.length < OVERRIDE_REASON_MIN) {
      setUnsaved(prev => ({ ...prev, [questionId]: true }));
      return;
    }
    await persistAnswer(questionId, value, needsOverrideReason(questionId, value) ? reason : null);
  };

  const handleReasonChange = (questionId, text) => {
    setOverrideReasons(prev => ({ ...prev, [questionId]: text }));
    setUnsaved(prev => ({ ...prev, [questionId]: true }));
  };

  const handleReasonBlur = async (questionId) => {
    const value = answers[questionId];
    const reason = (overrideReasons[questionId] || '').trim();
    if (value && reason.length >= OVERRIDE_REASON_MIN) {
      await persistAnswer(questionId, value, reason);
    }
  };

  const handleSaveAndContinueLater = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/assessment/${assessmentId}/save-progress`,
        { current_pillar: currentPillar + 1 },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success('Progress saved successfully! You can close this page and resume anytime.');
    } catch (error) {
      toast.error('Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const savePillarProgress = async (pillarIndex) => {
    if (!assessmentId) return;
    try {
      await axios.post(
        `${API}/assessment/${assessmentId}/save-progress`,
        { current_pillar: pillarIndex + 1 },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
    } catch (error) {
      // Silent save — don't disrupt navigation
    }
  };

  const handleNextPillar = () => {
    if (!isPillarComplete()) {
      const pendingReason = pillarQuestions.some(q => unsaved[q.id]);
      toast.error(pendingReason
        ? 'Please add a reason (15+ characters) wherever your answer differs from the scan suggestion'
        : 'Please answer all questions before proceeding');
      return;
    }
    
    if (currentPillar < pillars.length - 1) {
      const nextPillar = currentPillar + 1;
      setCurrentPillar(nextPillar);
      savePillarProgress(nextPillar);
      window.scrollTo(0, 0);
    } else {
      setShowConfirmDialog(true);
    }
  };

  const handlePreviousPillar = () => {
    if (currentPillar > 0) {
      const prevPillar = currentPillar - 1;
      setCurrentPillar(prevPillar);
      savePillarProgress(prevPillar);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/assessment/${assessmentId}/submit`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      toast.success('Assessment submitted successfully');
      navigate('/submission-complete', { state: { assessmentRef: response.data.assessment_ref } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit assessment');
    } finally {
      setSubmitting(false);
      setShowConfirmDialog(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#008080] mx-auto mb-4" />
          <p className="text-slate-600">Loading assessment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="CodeXero" className="h-10" />
          <div className="flex items-center gap-4">
            <span className="text-slate-600 text-sm hidden sm:inline">
              {user?.display_username}
            </span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              data-testid="logout-btn"
              className="text-slate-600 hover:text-red-600"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-slate-200 py-4">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">
              {answeredCount} of {totalQuestions} questions answered
            </span>
            <span className="text-sm font-medium text-[#003366]">
              {Math.round(progress)}% complete
            </span>
          </div>
          <Progress value={progress} className="h-2" data-testid="progress-bar" />
        </div>
      </div>

      {/* Pillar Stepper */}
      <div className="bg-white border-b border-slate-200 py-4 overflow-x-auto">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-2 min-w-max">
            {pillars.map((pillar, index) => {
              const pillarQs = questions.filter(q => q.pillar_code === pillar.code);
              const pillarAnswered = pillarQs.filter(q => answers[q.id]).length;
              const isComplete = pillarQs.length > 0 && pillarAnswered === pillarQs.length;
              const isCurrent = index === currentPillar;
              
              return (
                <div key={pillar.id} className="flex items-center">
                  <button
                    onClick={() => { setCurrentPillar(index); savePillarProgress(index); }}
                    data-testid={`pillar-step-${index + 1}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-sm transition-colors ${
                      isCurrent 
                        ? 'bg-[#008080] text-white' 
                        : isComplete 
                          ? 'bg-[#003366] text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {isComplete && !isCurrent ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </span>
                    )}
                    <span className="text-sm font-medium whitespace-nowrap hidden lg:inline">
                      {pillar.name.length > 15 ? pillar.name.substring(0, 15) + '...' : pillar.name}
                    </span>
                    <span className="text-sm font-medium lg:hidden">
                      P{index + 1}
                    </span>
                  </button>
                  {index < pillars.length - 1 && (
                    <div className="w-4 h-0.5 bg-slate-200 mx-1" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Questions */}
      <div className="max-w-3xl mx-auto px-6 py-8">
        {currentPillarData && (
          <>
            <div className="mb-8">
              <h1 className="text-2xl font-[800] text-[#003366] mb-2" data-testid="pillar-heading">
                {currentPillarData.name}
              </h1>
              <p className="text-slate-600">{currentPillarData.description}</p>
              <p className="text-sm text-[#008080] mt-2">
                DPDP Reference: {currentPillarData.dpdp_reference}
              </p>
            </div>

            <div className="space-y-6">
              {pillarQuestions.map((question, qIndex) => {
                // SDF v2.1 — surface dual-module sub-headers inside P9 so users
                // know they're moving from Designation Probability (Module A)
                // into Obligation Readiness (Module B).
                const isSdfModuleA = question.module === 'SDF_DESIGNATION_PROBABILITY';
                const isSdfModuleB = question.module === 'SDF_OBLIGATION_READINESS';
                const prevQ = qIndex > 0 ? pillarQuestions[qIndex - 1] : null;
                const showModuleAHeader = isSdfModuleA && (!prevQ || prevQ.module !== 'SDF_DESIGNATION_PROBABILITY');
                const showModuleBHeader = isSdfModuleB && (!prevQ || prevQ.module !== 'SDF_OBLIGATION_READINESS');

                return (
                <React.Fragment key={question.id}>
                  {showModuleAHeader && (
                    <div className="border-l-4 border-[#008080] bg-[#008080]/5 px-4 py-3 rounded-sm" data-testid="sdf-module-a-header">
                      <div className="text-xs font-[700] text-[#008080] uppercase tracking-wider mb-1">
                        Module A — Designation Probability
                      </div>
                      <p className="text-sm text-slate-600">
                        These 6 factors assess the probability the Central Government may designate
                        your organisation as a Significant Data Fiduciary under Section 10 of the DPDP Act 2023.
                      </p>
                    </div>
                  )}
                  {showModuleBHeader && (
                    <div className="border-l-4 border-[#003366] bg-[#003366]/5 px-4 py-3 rounded-sm" data-testid="sdf-module-b-header">
                      <div className="text-xs font-[700] text-[#003366] uppercase tracking-wider mb-1">
                        Module B — Obligation Readiness
                      </div>
                      <p className="text-sm text-slate-600">
                        These 8 obligations measure your readiness for Rule 13(1)–(5) of the DPDP Rules 2025.
                        They are scored only when Module A indicates moderate or higher designation probability.
                      </p>
                    </div>
                  )}
                  <div
                    className="bg-white border border-slate-200 rounded-sm p-6"
                    data-testid={`question-card-${question.question_number}`}
                  >
                  <div className="mb-4">
                    <span className="text-xs font-[700] text-[#008080] uppercase tracking-wider">
                      {question.question_number}
                    </span>
                    <h3 className="text-[#003366] font-medium mt-1">
                      {question.question_text}
                    </h3>
                  </div>

                  {(hintsByQuestion[question.id]?.all || []).map((hint) => (
                    <button
                      key={hint.hint_id}
                      type="button"
                      onClick={() => handleAnswerChange(question.id, hint.suggested_option)}
                      data-testid={`hint-chip-${question.question_number}-${hint.check_id}`}
                      className="w-full text-left mb-3 flex items-start gap-2 px-3 py-2 rounded-full border border-[#008080]/40 bg-[#008080]/5 hover:bg-[#008080]/10 transition-colors"
                      title="Click to select the suggested option"
                    >
                      <ScanSearch className="h-4 w-4 text-[#008080] mt-0.5 shrink-0" />
                      <span className="text-sm text-slate-700">
                        <span className="font-[700] text-[#008080]">
                          Scan suggests: {hint.suggested_option}
                        </span>
                        {' · '}{hint.evidence_text}
                      </span>
                    </button>
                  ))}

                  {(confirmationsByQuestion[question.id] || []).map((c) => (
                    <div
                      key={c.confirmation_id}
                      data-testid={`confirm-chip-${question.question_number}-${c.check_id}`}
                      className="w-full text-left mb-3 flex items-start gap-2 px-3 py-2 rounded-full border border-green-200 bg-green-50"
                    >
                      <CheckCircle2 className="h-4 w-4 text-green-700 mt-0.5 shrink-0" />
                      <span className="text-sm text-slate-700">
                        <span className="font-[700] text-green-700">Scan found:</span>
                        {' '}{c.evidence_text}
                      </span>
                    </div>
                  ))}

                  <RadioGroup
                    value={answers[question.id] || ''}
                    onValueChange={(value) => handleAnswerChange(question.id, value)}
                    className="space-y-3"
                  >
                    {['A', 'B', 'C', 'D'].filter(option => question[`option_${option.toLowerCase()}`]).map((option) => (
                      <div
                        key={option}
                        className={`question-option flex items-start gap-3 p-4 border rounded-sm cursor-pointer ${
                          answers[question.id] === option ? 'selected border-[#008080] bg-[#008080]/5' : 'border-slate-200'
                        }`}
                        onClick={() => handleAnswerChange(question.id, option)}
                      >
                        <RadioGroupItem 
                          value={option} 
                          id={`${question.id}-${option}`}
                          data-testid={`${question.question_number}-option-${option}`}
                        />
                        <Label 
                          htmlFor={`${question.id}-${option}`}
                          className="cursor-pointer flex-1"
                        >
                          <span className="font-medium text-[#003366] mr-2">{option}.</span>
                          <span className="text-slate-700">
                            {question[`option_${option.toLowerCase()}`]}
                          </span>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>

                  {needsOverrideReason(question.id, answers[question.id]) && (
                    <div className="mt-4 border-l-4 border-amber-400 bg-amber-50 p-4 rounded-sm"
                         data-testid={`override-reason-block-${question.question_number}`}>
                      <Label htmlFor={`reason-${question.id}`} className="text-sm text-amber-900">
                        Your answer differs from the scan suggestion ({hintFor(question.id)?.suggested_option}).
                        Please tell us why — minimum {OVERRIDE_REASON_MIN} characters. Required before this
                        answer is saved.
                      </Label>
                      <Textarea
                        id={`reason-${question.id}`}
                        value={overrideReasons[question.id] || ''}
                        onChange={(e) => handleReasonChange(question.id, e.target.value)}
                        onBlur={() => handleReasonBlur(question.id)}
                        placeholder="e.g. The scan missed our consent banner because it loads only for EU visitors"
                        className="mt-2 bg-white"
                        data-testid={`override-reason-input-${question.question_number}`}
                      />
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-amber-800">
                          {(overrideReasons[question.id] || '').trim().length}/{OVERRIDE_REASON_MIN} characters
                        </span>
                        {unsaved[question.id] ? (
                          <span className="text-xs text-amber-800" data-testid={`answer-unsaved-${question.question_number}`}>
                            Not saved yet
                          </span>
                        ) : (
                          <span className="text-xs text-green-700" data-testid={`answer-saved-${question.question_number}`}>
                            Saved
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                </React.Fragment>
                );
              })}
            </div>

            {/* Navigation */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-8 pt-6 border-t border-slate-200">
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handlePreviousPillar}
                  disabled={currentPillar === 0}
                  data-testid="previous-pillar-btn"
                  className="rounded-sm"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Previous Pillar
                </Button>
                <Button
                  variant="outline"
                  onClick={handleSaveAndContinueLater}
                  disabled={saving}
                  data-testid="save-continue-later-btn"
                  className="rounded-sm"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save & Continue Later
                </Button>
              </div>
              
              <Button
                onClick={handleNextPillar}
                disabled={!isPillarComplete()}
                data-testid="next-pillar-btn"
                className="bg-[#003366] hover:bg-[#002244] text-white rounded-sm btn-hover"
              >
                {currentPillar === pillars.length - 1 ? 'Submit Assessment' : 'Next Pillar'}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-[#003366]">Confirm Submission</DialogTitle>
            <DialogDescription>
              You have answered all questions. Once submitted, your answers cannot be changed. Are you sure you want to submit?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowConfirmDialog(false)}
              data-testid="cancel-submit-btn"
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="confirm-submit-btn"
              className="bg-[#003366] hover:bg-[#002244] text-white rounded-sm"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Confirm & Submit'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-[#003366] text-white py-6 mt-auto">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-400 text-sm">© CodeXero {new Date().getFullYear()}</p>
        </div>
      </footer>
    </div>
  );
};

export default AssessmentPage;
